/**
 * Daily GHG breakdown table used by CompanyGHGPage.
 * Renders a loading skeleton, a data table, or an "empty" alert depending on state.
 * Eliminates the internal duplication of the identical table header/skeleton structure
 * that previously appeared twice (once for loading, once for data).
 */

import React from 'react';
import {
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { formatNumber } from '../../utils/numberFormat';
import { formatEmissions } from './ghgStatsUtils';

interface Props {
  loading: boolean;
  data: Array<any>;
}

const GhgDailyBreakdownTable: React.FC<Props> = ({ loading, data }) => {
  const columns = (
    <TableRow>
      <TableCell>Date</TableCell>
      <TableCell align="right">TOT (Tonnes)</TableCell>
      <TableCell align="right">OPS (Tonnes)</TableCell>
      <TableCell align="right">ENE (Tonnes)</TableCell>
      <TableCell align="right">EI (gr/tkm)</TableCell>
      <TableCell align="right">TKM</TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>{columns}</TableHead>
          <TableBody>
            {Array.from({ length: 10 }, (_, i) => `daily-skeleton-${i}`).map((rowKey) => (
              <TableRow key={rowKey}>
                <TableCell><Skeleton variant="text" width={120} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={70} sx={{ ml: 'auto' }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (data.length > 0) {
    return (
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>{columns}</TableHead>
          <TableBody>
            {data.map((day: any) => (
              <TableRow key={day.day} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {format(new Date(day.day), 'MMM dd, yyyy')}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatEmissions(day.total_tot_emissions)}</TableCell>
                <TableCell align="right">{formatEmissions(day.total_ops_emissions)}</TableCell>
                <TableCell align="right">{formatEmissions(day.total_ene_emissions)}</TableCell>
                <TableCell align="right">{formatNumber(day.total_ei || 0, 2)}</TableCell>
                <TableCell align="right">{formatNumber(day.total_tkm || 0)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return <Alert severity="info">No daily data available for this month</Alert>;
};

export default GhgDailyBreakdownTable;
